import { EvaluationAutoCompleteDTO } from './models/evaluationAutoCompleteDTO';
import { NewConsultationDto } from './../patient.model';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';

import { ApiService } from '@api/core';
import {
  FoldsEvaluation,
  ControlEvaluation,
  ReducedEvaluation,
  Evaluation,
  EvaluationTypes,
  EvaluationType,
  ControlMeasurement,
  BioODexaEvaluation,
} from '@api/evaluation';
import { PatientEvaluation } from '@api/patient/evaluation';
import { catchError, tap } from 'rxjs/operators';
import { ErrorHandlerService } from '@app/shared/errors/services/error-handler.service';

const API_URL = 'new_evaluation';


@Injectable()
export class PatientEvaluationApiService {
  private dataSubject = new BehaviorSubject<File>(null);
  fileBioDexa$ = this.dataSubject.asObservable();
  constructor(private api: ApiService, private errorHandler: ErrorHandlerService) { }

  setFileBioDexa(file: File) {
    this.dataSubject.next(file);
  }


  all(patientId: number): Observable<PatientEvaluation[]> {
    return this.api.get<PatientEvaluation[]>(API_URL, `/${patientId}`);
  }

  viewFA2C(patientId: number): Observable<PatientEvaluation[]> {
    return this.api.get<PatientEvaluation[]>(API_URL, `/viewFA2C/${patientId}`);
  }
  viewFA5C(patientId: number): Observable<PatientEvaluation[]> {
    return this.api.get<PatientEvaluation[]>(API_URL, `/viewFA5C/${patientId}`);
  }

  allTypes(): Observable<EvaluationType[]> {
    return of(EvaluationTypes.All);
  }

  // TODO: remove evaluation_detail and configure environment correctly
  getEvaluationDetail(evaluationId, instituteId, patientId) {
    return this.api.
      get<ControlEvaluation>(API_URL, `/${evaluationId}?instituteId=${instituteId}&patientId=${patientId}`);
  }

  getEvaluationnById(evaluationId: number, instituteId: number, patientId: number): Observable<EvaluationAutoCompleteDTO> {
    return this.api.get(API_URL, `/${evaluationId}?instituteId=${instituteId}&patientId=${patientId}`);
  }

  get(
    type: string,
    id: number,
  ): Observable<
    | FoldsEvaluation
    | ControlEvaluation
    | ReducedEvaluation
    | Evaluation
    | ControlMeasurement
  > {
    switch (type.toUpperCase()) {
      case EvaluationTypes.FOLDS.key:
        return this.getFolds(id);
      case EvaluationTypes.CONTROL.key:
        return this.getControl(id);
      case EvaluationTypes.REDUCE.key:
        return this.getReduced(id);
      case EvaluationTypes.COMPLETE.key:
        return this.getComplete(id);
      case EvaluationTypes.CONTROL_WEIGHT.key:
        return this.getControlWeight(id);
      default:
        return of(null);
    }
  }

  getFolds(id: number): Observable<FoldsEvaluation> {
    return this.api.get<FoldsEvaluation>(API_URL, `/fold/${id}`);
  }

  getControl(id: number): Observable<ControlEvaluation> {
    return this.api.get<ControlEvaluation>(API_URL, `/control/${id}`);
  }

  getReduced(id: number): Observable<ReducedEvaluation> {
    return this.api.get<ReducedEvaluation>(API_URL, `/reduce/${id}`);
  }

  getComplete(id: number): Observable<Evaluation> {
    return this.api.get<Evaluation>(API_URL, `/complete/${id}`);
  }

  getControlWeight(id: number): Observable<ControlEvaluation> {
    return this.api.get<ControlEvaluation>(API_URL, `/weight-control/${id}`);
  }

  add(
    type: string,
    doctorId: number,
    patientId: number,
    evaluation:
      | FoldsEvaluation
      | ControlEvaluation
      | ReducedEvaluation
      | BioODexaEvaluation
      | ControlEvaluation
      | Evaluation,
    creationDate?: string
  ): Observable<any> {
    switch (type) {
      case EvaluationTypes.FOLDS.key:
        return this.addFolds(
          doctorId,
          patientId,
          evaluation as FoldsEvaluation,
          creationDate
        );
      case EvaluationTypes.BIOEXA.key:
        return this.addBioExa(
          doctorId,
          patientId,
          evaluation as BioODexaEvaluation,
          creationDate
        );
      case EvaluationTypes.CONTROL.key:
        return this.addControl(
          doctorId,
          patientId,
          evaluation as ControlEvaluation,
          creationDate
        );
      case EvaluationTypes.REDUCE.key:
        return this.addReduced(
          doctorId,
          patientId,
          evaluation as ReducedEvaluation,
          creationDate
        );
      case EvaluationTypes.COMPLETE.key:
        return this.addComplete(doctorId, patientId, evaluation as Evaluation, creationDate);
      case EvaluationTypes.CONTROL_WEIGHT.key:
        return this.addWeightControl(
          doctorId,
          patientId,
          evaluation as ControlMeasurement,
          creationDate
        );
      default:
        return of(null);
    }
  }
  update(
    type: string,
    evaluationId: number,
    patientId: number,
    evaluation:
      | FoldsEvaluation
      | ControlEvaluation
      | ReducedEvaluation
      | BioODexaEvaluation
      | ControlEvaluation
      | Evaluation,
  ): Observable<any> {
    switch (type) {
      case EvaluationTypes.FOLDS.key:
        return this.updateFolds(
          evaluationId,
          patientId,
          evaluation as FoldsEvaluation,
        );
      case EvaluationTypes.BIOEXA.key:
        return this.updateBioExa(
          evaluationId,
          patientId,
          evaluation as BioODexaEvaluation,
        );
      case EvaluationTypes.CONTROL.key:
        return this.updateControl(
          evaluationId,
          patientId,
          evaluation as ControlEvaluation);
      case EvaluationTypes.REDUCE.key:
        return this.updateReduced(
          evaluationId,
          patientId,
          evaluation as ReducedEvaluation,
        );
      case EvaluationTypes.COMPLETE.key:
        return this.updateComplete(
          evaluationId,
          patientId,
          evaluation as Evaluation);
      case EvaluationTypes.CONTROL_WEIGHT.key:
        return this.updateWeightControl(
          evaluationId,
          patientId,
          evaluation as ControlMeasurement,
        );
      default:
        return of(null);
    }
  }

  addFolds(
    doctorId: number,
    patientId: number,
    evaluation: FoldsEvaluation,
    creationDate?: string
  ): Observable<any> {
    const body = { ...evaluation, creationDate };
    return this.api.post(
      API_URL,
      `/fold?doctorId=${doctorId}&patientId=${patientId}`,
      body
    );
  }

  updateFolds(
    evaluationId: number,
    patientId: number,
    evaluation: FoldsEvaluation,
  ): Observable<any> {
    evaluation['patientId'] = patientId

    return this.api.put(
      API_URL,
      `/fold/${evaluationId}`,
      evaluation,
    );
  }

  addControl(
    doctorId: number,
    patientId: number,
    evaluation: ControlEvaluation,
    creationDate?: string
  ): Observable<any> {
    const body = { ...evaluation, creationDate };
    return this.api.post(
      API_URL,
      `/control?doctorId=${doctorId}&patientId=${patientId}`,
      body,
    ).pipe(
      catchError((error) => {
        if (error && error.status === 599) {
          this.errorHandler.showErrorModal('Debe ingresar una evaluación de FA5C Completa o Reducida previamente a una evaluación de FA5C de Control.');
          throw error;
        }
        throw error;
      })
    );
  }

  updateControl(
    evaluationId: number,
    patientId: number,
    evaluation: ControlEvaluation,
  ): Observable<any> {
    evaluation['patientId'] = patientId

    return this.api.put(
      API_URL,
      `/control/${evaluationId}`,
      evaluation,
    );
  }

  addReduced(
    doctorId: number,
    patientId: number,
    evaluation: ReducedEvaluation,
    creationDate: string
  ): Observable<any> {
    const body = { ...evaluation, creationDate };
    return this.api.post(
      API_URL,
      `/reduce?doctorId=${doctorId}&patientId=${patientId}`,
      body,
    );
  }

  updateReduced(
    evaluationId: number,
    patientId: number,
    evaluation: ReducedEvaluation,
  ): Observable<any> {
    evaluation['patientId'] = patientId

    return this.api.put(
      API_URL,
      `/reduce/${evaluationId}`,
      evaluation,
    );
  }
  addComplete(
    doctorId: number,
    patientId: number,
    evaluation: Evaluation,
    creationDate?: string
  ): Observable<any> {
    const body = { ...evaluation, creationDate };
    return this.api.post(
      API_URL,
      `/complete?doctorId=${doctorId}&patientId=${patientId}`,
      body,
    );
  }

  updateComplete(
    evaluationId: number,
    patientId: number,
    evaluation: Evaluation,
  ): Observable<any> {
    evaluation['patientId'] = patientId

    return this.api.put(
      API_URL,
      `/complete/${evaluationId}`,
      evaluation,
    );
  }
  addWeightControl(
    doctorId: number,
    patientId: number,
    evaluation: ControlMeasurement,
    creationDate?: string
  ): Observable<any> {
    const body = { ...evaluation, creationDate };
    return this.api.post(
      API_URL,
      `/simple?doctorId=${doctorId}&patientId=${patientId}`,
      body,
    );
  }

  updateWeightControl(
    evaluationId: number,
    patientId: number,
    evaluation: ControlMeasurement,
  ): Observable<any> {
    evaluation['patientId'] = patientId
    return this.api.put(
      API_URL,
      `/simple/${evaluationId}`,
      evaluation,
    );
  }

  addBioExa(
    doctorId: number,
    patientId: number,
    evaluation: BioODexaEvaluation,
    creationDate?: string
  ): Observable<any> {
    const body = { ...evaluation, creationDate };
    //  evaluation.bioODexaMeasurement['bioODexaSubType'] = 'BIO'
    return this.api.post(
      API_URL,
      `/bioOexa?doctorId=${doctorId}&patientId=${patientId}`,
      body
    );
  }
  updateBioExa(
    evaluationId: number,
    patientId: number,
    evaluation: BioODexaEvaluation,
  ): Observable<any> {
    evaluation['patientId'] = patientId

    //  evaluation.bioODexaMeasurement['bioODexaSubType'] = 'BIO'
    return this.api.put(
      API_URL,
      `/bioOexa/${evaluationId}`,
      evaluation
    );
  }

}
